// Phase 43-09 — <CanvasEquipmentSection /> (CANVAS-01 gap closure).
// Re-implements equipment list as two field-arrays (standard_equipment +
// optional_equipment) using react-hook-form's useFieldArray. Mirrors the
// wizard's two-tab pattern from SpecsAndConfigStep but as two stacked
// sub-sections rather than Tabs (the canvas form is a single scroll, no
// nested tabs).
//
// Drag-to-reorder (the wizard's @dnd-kit/sortable layer) is OUT OF SCOPE
// for gap closure — Add/Remove only.
//
// D-15 (V1 era): the wizard folder was never modified by the canvas work. Type-only import of
// WizardFormData is allowed.
//
// Typography lock (UI-SPEC rev 2): 13px labels font-normal, 14px input
// body font-normal, 16px section headings font-semibold (we use h3 16px
// for the two sub-section headings to keep visual hierarchy distinct from
// the parent <SectionHeading> h2 in CanvasFormSections). The 12px size and
// 500-weight Tailwind utility class names are disallowed by UI-SPEC and
// appear nowhere in this file.

import { useFormContext, useFieldArray, useWatch, type UseFieldArrayReturn } from 'react-hook-form';
import { Plus, X, ArrowLeftRight } from 'lucide-react';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import { useContext } from 'react';
import type { NotImportedOptionLine } from '@/modules/inventory/types/boat.types';
import { NotImportedOptionsList } from '@/modules/inventory/components/equipment/NotImportedOptionsList';
import { EquipmentTotalsFooter } from '@/modules/inventory/components/equipment/EquipmentTotalsFooter';
import { CanvasReviewContext } from '../CanvasReview';

type EquipmentFieldName = 'standard_equipment' | 'optional_equipment';

// #1286 — a typed amount is kept as a number, never the text: changes-only autosave
// compares it with the server's decimal:2 string ("1500.00"), and sameValue matches
// a number to a numeric string but not "1500" to "1500.00", which would re-send forever.
const toAmount = (value: unknown): number | null =>
  value === '' || value === null || value === undefined ? null : Number(value);

const MONEY_FIELDS = [
  { key: 'cost', label: 'Cost' },
  { key: 'price', label: 'Price' },
] as const;

interface EquipmentSubListProps {
  name: EquipmentFieldName;
  label: string;
  regionId: string;
  moveLabel: string;
  array: UseFieldArrayReturn<WizardFormData, EquipmentFieldName>;
  onMove: (idx: number) => void;
}

function EquipmentSubList({ name, label, regionId, moveLabel, array, onMove }: EquipmentSubListProps) {
  const { register, control } = useFormContext<WizardFormData>();
  const { fields, append, remove } = array;
  const liveRows = useWatch({ control, name }) ?? [];

  const handleAdd = () => {
    append({ item: '', price: null, cost: null, sort_order: fields.length });
  };

  const headingId = `${regionId}-heading`;

  return (
    <section
      role="region"
      aria-labelledby={headingId}
      className="space-y-3"
    >
      <h3
        id={headingId}
        className="text-base font-semibold"
      >
        {label}
      </h3>
      {fields.length === 0 ? (
        <p className="text-[0.8125rem] font-normal text-muted-foreground">
          No equipment items added.
        </p>
      ) : (
        <div className="space-y-2">
          {fields.map((field, idx) => {
            const inputId = `${regionId}-item-${idx}`;
            return (
              <div
                key={field.id}
                className="flex items-end gap-2 rounded-md border p-3"
              >
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor={inputId}
                    className="text-[0.8125rem] font-normal"
                  >
                    Item
                  </Label>
                  <Input
                    id={inputId}
                    type="text"
                    className="text-sm font-normal"
                    {...register(`${name}.${idx}.item`)}
                  />
                </div>
                {MONEY_FIELDS.map(({ key, label: moneyLabel }) => (
                  <div key={key} className="w-28 space-y-1">
                    <Label htmlFor={`${inputId}-${key}`} className="text-[0.8125rem] font-normal">
                      {moneyLabel}
                    </Label>
                    <Input
                      id={`${inputId}-${key}`}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      placeholder="0.00"
                      aria-label={`${moneyLabel}, ${liveRows[idx]?.item || `item ${idx + 1}`}`}
                      className="text-sm font-normal tabular-nums"
                      {...register(`${name}.${idx}.${key}`, { setValueAs: toAmount })}
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={moveLabel}
                  onClick={() => onMove(idx)}
                >
                  <ArrowLeftRight className="size-3.5 me-1" />
                  {moveLabel}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Remove item"
                  onClick={() => remove(idx)}
                >
                  <X className="size-3.5 me-1" />
                  Remove
                </Button>
              </div>
            );
          })}
          <EquipmentTotalsFooter rows={liveRows} className="text-right text-[0.8125rem]" />
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAdd}
      >
        <Plus className="size-3.5 me-1" />
        Add item
      </Button>
    </section>
  );
}

export function CanvasEquipmentSection() {
  const { control, getValues } = useFormContext<WizardFormData>();
  const standard = useFieldArray({ control, name: 'standard_equipment' });
  const optional = useFieldArray({ control, name: 'optional_equipment' });

  const moveStandardToOptional = (idx: number) => {
    const value = getValues(`standard_equipment.${idx}`);
    if (value === undefined) return;
    standard.remove(idx);
    optional.append(value);
  };

  const moveOptionalToStandard = (idx: number) => {
    const value = getValues(`optional_equipment.${idx}`);
    if (value === undefined) return;
    optional.remove(idx);
    standard.append(value);
  };

  // #1182 — option lines the merge skipped; "Add" appends a dealer row that the
  // changes-only autosave sends. A line whose item and SKU match a form row is
  // hidden until the server stops listing it.
  const notImported = useContext(CanvasReviewContext)?.notImportedOptions ?? [];
  const sameText = (a: string | null | undefined, b: string | null | undefined) =>
    (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
  const rows = [...(getValues('standard_equipment') ?? []), ...(getValues('optional_equipment') ?? [])];
  const isPulledBack = (line: NotImportedOptionLine) =>
    rows.some((row) => sameText(row.item, line.item) && sameText(row.sku, line.sku));
  const pullBack = (line: NotImportedOptionLine) => {
    const target = line.type === 'optional' ? optional : standard;
    target.append({
      item: line.item,
      description: line.description,
      sku: line.sku,
      price: line.price,
      cost: line.cost,
      sort_order: target.fields.length,
    });
  };

  return (
    <div className="space-y-6">
      <EquipmentSubList
        name="standard_equipment"
        label="Standard equipment"
        regionId="canvas-equipment-standard"
        moveLabel="Make optional"
        array={standard}
        onMove={moveStandardToOptional}
      />
      <EquipmentSubList
        name="optional_equipment"
        label="Optional equipment"
        regionId="canvas-equipment-optional"
        moveLabel="Make standard"
        array={optional}
        onMove={moveOptionalToStandard}
      />
      <NotImportedOptionsList lines={notImported} onPullBack={pullBack} isPulledBack={isPulledBack} />
    </div>
  );
}
